import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  DialogActions,
  IconButton,
} from "@mui/material";
import { api } from "../../../redux/api";
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { newTemplateActions } from "../../../redux/slices/newTemplateSlice";
import { treeActions } from "../../../redux/slices/treeSlice";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { UploadArea, FilePreview } from "../../Common/StyledUploadComponents";

const UploadTemplate = ({ open, toggle }) => {
  const { t } = useTranslation();
  const { account } = useSelector((state) => state.queryParams);
  const parentScreen = useContext(ParentScreenCtx);
  const selectedNode = useSelector(
    (state) => state.treeData[parentScreen].leftTree.tree.body.selectedNode
  );
  const workflowGroupsTree = useSelector(
    (state) => state.treeData?.[parentScreen]?.leftTree?.tree?.body?.translatedData
  );
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const workflowGroup = selectedNode?.type === "workflowGroup"  ? selectedNode?.title :  selectedNode?.workflowGroup; 
  const fileInputRef = useRef(null);
  const allowedTypes = [
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/jpg",
    "text/html",
    "application/xml",
    "application/json",
    "text/xml",
    "text/json",
    "application/pdf"
  ];

  const showUploadError = (message) => {
    dispatch(newTemplateActions.setShowAlert(true));
    dispatch(newTemplateActions.setAlertType("error"));
    dispatch(newTemplateActions.setAlertMessage(message));
  };

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const templateExistsInWorkflowGroup = (selectedFile) => {
    const fileName = (selectedFile?.name || "").trim();
    if (!fileName || !workflowGroup) return false;

    const lastDot = fileName.lastIndexOf(".");
    const baseName = lastDot > 0 ? fileName.slice(0, lastDot).trim() : fileName;
    const candidates = [fileName, baseName]
      .filter(Boolean)
      .map((s) => s.toLowerCase());

    const groups = Array.isArray(workflowGroupsTree) ? workflowGroupsTree : [];
    const groupNode = groups.find(
      (n) => n?.type === "workflowGroup" && n?.title === workflowGroup
    );
    const children = Array.isArray(groupNode?.children) ? groupNode.children : [];

    return children.some((child) => {
      const existingTitle = (child?.title || "").trim().toLowerCase();
      return existingTitle && candidates.includes(existingTitle);
    });
  };

  const getJsonTemplateKind = (jsonText) => {
    try {
      const obj = JSON.parse(jsonText);
      if (obj?.WORKFLOW_DEFINITION) return "workflow";
      const header = obj?.TEMPLATE?.TEMPLATE_HEADER || {};
      if (header?.TEMPLATENAME) return "outbound";
      if (header?.TEMPLATE) return "inbound";
      if (obj?.TEMPLATE?.FILE_HEADER || obj?.TEMPLATE?.FILE_TRAILER) return "outbound";
      if (obj?.TEMPLATE?.HEADER || obj?.TEMPLATE?.TRAILER) return "inbound";
      return "unknown";
    } catch (err) {
      return "invalid_json";
    }
  };

  const validateJsonTemplateType = async (selectedFile) => {
    const fileName = selectedFile?.name || "";
    const isJsonByName = fileName.toLowerCase().endsWith(".json");
    const isJsonByType = ["application/json", "text/json"].includes(selectedFile?.type || "");
    if (!isJsonByName && !isJsonByType) return true;

    const jsonText = await selectedFile.text();
    const kind = getJsonTemplateKind(jsonText);
    if (kind === "invalid_json") {
      showUploadError("Invalid JSON file. Please select a valid JSON template.");
      return false;
    }
    if (kind === "unknown") {
      showUploadError("Unknown JSON template type. Please upload a valid inbound, outbound, or workflow template.");
      return false;
    }

    if (kind !== parentScreen) {
      const kindLabel = kind.charAt(0).toUpperCase() + kind.slice(1);
      const screenLabel = parentScreen.charAt(0).toUpperCase() + parentScreen.slice(1);
      showUploadError(`This file appears to be a ${kindLabel} template. Please upload under the ${kindLabel} tab (you are on ${screenLabel}).`);
      return false;
    }

    return true;
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files && e.target.files[0];
    if (selectedFile) {
      if (allowedTypes.includes(selectedFile.type)) {
        const isValid = await validateJsonTemplateType(selectedFile);
        if (!isValid) {
          setFile(null);
          resetFileInput();
          return;
        }
        setFile(selectedFile);
      } else {
        showUploadError(
          "Invalid file type. Please upload PNG, JPEG, GIF, JPG, PDF, HTML, XML or JSON."
        );
        resetFileInput();
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      dispatch(newTemplateActions.setShowAlert(true));
      dispatch(newTemplateActions.setAlertType("error"));
      dispatch(
        newTemplateActions.setAlertMessage("Please select a file first.")
      );
      return;
    }

    if (templateExistsInWorkflowGroup(file)) {
      showUploadError(t("BOX_UPLOAD_TEMPLATE_NAME_ALREADY_EXISTS_IN_WF_GROUP"));
      setFile(null);
      resetFileInput();
      return;
    }

    setIsLoading(true);

    let formData = new FormData();
    formData.append("file", file);
    let dispatchTree =
      parentScreen === "outbound"
        ? "getOutboundTemplateList"
        : parentScreen === "inbound"
        ? "getInboundTemplateList"
        : "getWorkflows";

    try {
      const response = await dispatch(
        api.endpoints.uploadTemplate.initiate({
          file_name: file?.name,
          workflow_group_name: encodeURIComponent(workflowGroup),
          account: account,
          file: formData,
          type:
            parentScreen === "inbound"
              ? "INBOUND"
              : parentScreen === "outbound"
              ? "OUTBOUND"
              : "WORKFLOW",
        })
      );
      if (response.error) {
        throw new Error(response.error.message || "Upload failed.");
      }
      if (response?.data?.current_state === "SUCCESS") {
        dispatch(newTemplateActions.setShowAlert(true));
        dispatch(newTemplateActions.setAlertType("success"));
        dispatch(
          newTemplateActions.setAlertMessage(
            response.data.message || "File uploaded successfully!"
          )
        );
        dispatch(
          treeActions.setLeftTreeContextActionValue({
            screen: parentScreen,
            data: {
              name: response?.data?.data?.template_name,
              workflowGroup: workflowGroup,
            },
          })
        );
        dispatch(
          api.endpoints[dispatchTree].initiate(
            { account },
            { subscribe: false, forceRefetch: true }
          )
        );
        setFile(null);
        setIsLoading(false);
        toggle();
      } else {
        dispatch(newTemplateActions.setShowAlert(true));
        dispatch(newTemplateActions.setAlertType("error"));
        dispatch(
          newTemplateActions.setAlertMessage(
            response.data.message || "Upload failed. Please try again."
          )
        );
        setIsLoading(false);
        // toggle();
      }
    } catch (err) {
      dispatch(newTemplateActions.setShowAlert(true));
      dispatch(newTemplateActions.setAlertType("error"));
      dispatch(
        newTemplateActions.setAlertMessage("Upload failed. Please try again.")
      );
      setIsLoading(false);
      // toggle();
    } finally {
      setIsLoading(false);
    }
  };
  const resetForm = () => {
    setFile(null);
  };
  useEffect(() => {
   if(!open) {
      resetForm();
    }
  },[open])
  return (
    <>
      <Dialog
        open={open}
        onClose={toggle}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden" },
        }}>
        <DialogTitle
          className="dialog-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <div className="title">Upload Template</div>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => {
              toggle();
            }}
            aria-label="close"
            size="small">
            <CloseIcon
              sx={{
                height: "28px",
                width: "28px",
                color: "#777777",
              }}
            />
          </IconButton>
        </DialogTitle>
        <DialogContent className="dialog-content" dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
              Select a file to upload
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Allowed file types: PNG, JPEG, GIF, JPG, PDF, HTML, XML or JSON.
            </Typography>
          </Box>
          <UploadArea onClick={() => fileInputRef.current.click()}>
            <input
              type="file"
              hidden
              onChange={handleFileChange}
              ref={fileInputRef}
            />

            <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              {"Click to browse"}
            </Typography>
          </UploadArea>
          {file && (
            <FilePreview>
              <FileIcon color="primary" sx={{ mr: 1.5 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" noWrap>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
              <IconButton size="small" onClick={resetForm}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </FilePreview>
          )}
        </DialogContent>
        <DialogActions>
          <div >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleUpload}
              style={{ marginRight: "10px" }}
              disabled={!file || isLoading}>
              {isLoading ? <CircularProgress size='16px' /> : "Upload"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => toggle()}
              style={{ marginRight: "10px" }}
              color="primary">
              {t("CANCEL")}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadTemplate;
